import React, { useEffect, useState } from 'react';
import { usePartnrStore, PartnrChat } from '@/stores/partnrStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { Sparkles, MessageSquare, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { formatDistanceToNow } from 'date-fns';

export function PartnrChatHistory() {
  const { chatHistory, fetchChatHistory, reopenChat, deleteChat } = usePartnrStore();
  const { currentBoardId } = useCanvasStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadHistory = async () => {
      if (currentBoardId) {
        setIsLoading(true);
        await fetchChatHistory(currentBoardId);
        setIsLoading(false);
      }
    };
    loadHistory();
  }, [currentBoardId, fetchChatHistory]);

  if (isLoading) {
    return (
      <div className="absolute bottom-full left-0 mb-2 w-[340px] bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
        <div className="p-3 border-b border-border bg-partnr/10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-partnr" />
            <span className="font-medium text-sm">Chat History</span>
          </div>
        </div>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (chatHistory.length === 0) {
    return (
      <div className="absolute bottom-full left-0 mb-2 w-[340px] bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
        <div className="p-3 border-b border-border bg-partnr/10">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-partnr" />
            <span className="font-medium text-sm">Chat History</span>
          </div>
        </div>
        <div className="p-6 text-center text-muted-foreground">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No conversations yet</p>
          <p className="text-xs mt-1">Start chatting with Partnr!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute bottom-full left-0 mb-2 w-[340px] bg-popover border border-border rounded-lg shadow-lg overflow-hidden">
      <div className="p-3 border-b border-border bg-partnr/10">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-partnr" />
          <span className="font-medium text-sm">Chat History</span>
          <span className="text-xs text-muted-foreground ml-auto">
            {chatHistory.length} conversation{chatHistory.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      <ScrollArea className="max-h-[300px]">
        <div className="p-2 space-y-1">
          {chatHistory.map((chat) => (
            <ChatHistoryItem 
              key={chat.id} 
              chat={chat}
              onReopen={() => reopenChat(chat)}
              onDelete={() => deleteChat(chat.id)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

interface ChatHistoryItemProps {
  chat: PartnrChat;
  onReopen: () => void;
  onDelete: () => void;
}

function ChatHistoryItem({ chat, onReopen, onDelete }: ChatHistoryItemProps) {
  const timeAgo = formatDistanceToNow(new Date(chat.updated_at), { addSuffix: true });
  
  return (
    <div className="group flex items-start gap-2 p-2 rounded-md hover:bg-muted cursor-pointer transition-colors">
      <div 
        className="flex-1 min-w-0 overflow-hidden"
        onClick={onReopen}
      >
        <p className="text-sm font-medium truncate">
          {chat.title || 'Untitled conversation'}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">
            {timeAgo}
          </span>
          {chat.referenced_item_ids.length > 0 && (
            <span className="text-xs text-partnr">
              {chat.referenced_item_ids.length} item{chat.referenced_item_ids.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            onReopen();
          }}
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
